import { StatusCode } from "api/protocols";
import { GetBookingDeposit, IBookingDeposit } from "api/rpc/2024-04/facilityAdmin/deposit/deposit";
import axios, { CancelToken } from "axios";
import Sheet from "components/sheet/Sheet";
import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import { useAppDispatch } from "hooks/redux";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { showError } from "redux/actions/ui";
import "./customerDepositsModal.scss";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import { LocaleCurrency } from "helpers/Locale";
import moment from "moment";
import Input from "components/form/input";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import MessageDisplay from "components/messageDisplay/MessageDisplay";

interface IProps {
    open: boolean;
    onOk: (deposit: IBookingDeposit, amountToApply: string) => void;
    onClose: () => void;
    customerId: number;
    cartAmount: string;
}

export default function CustomerDepositsModal(props: IProps) {
    const { open, onOk, onClose, customerId, cartAmount } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [deposits, setDeposits] = useState<Array<IBookingDeposit>>(undefined);
    const [selectedDeposit, setSelectedDeposit] = useState<IBookingDeposit>(null);
    const [applyAmount, setApplyAmount] = useState<string>("");
    const { getCurrencySymbol } = useCurrencyFacility();

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (open) {
            void getDeposits(source.token);
        }
        return () => source.cancel();
    }, [open]);

    async function getDeposits(cancelToken?: CancelToken) {
        const res = await GetBookingDeposit({ customer_id: customerId, remaining_only: true, refunds: true }, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting deposits")));
            setDeposits([]);
            return;
        }

        setDeposits(res?.data?.filter(deposit => deposit?.amount_remaining > 0));
    }

    function handleClose() {
        ReactDOM.unstable_batchedUpdates(() => {
            setDeposits(undefined);
            setSelectedDeposit(null);
            setApplyAmount("");
        });

        void onClose();
    }

    function handleOk() {
        void onOk(selectedDeposit, applyAmount);
        void handleClose();
    }

    return (
        <Portal isMounted={open}>
            <Sheet
                open={open}
                title={t("Deposits")}
                size="small"
                onCancel={handleClose}
                onOk={handleOk}
                closable
                okDisabled={!selectedDeposit || Number(applyAmount) <= 0 || Number(applyAmount) > selectedDeposit?.amount_remaining}
                okText={t("Apply")}
                stacked
            >
                {deposits === undefined ? (
                    <div className="flex w-100 justify-center"><Spin /></div>
                ) : deposits?.length === 0 ? (
                    <MessageDisplay title={t("No deposits available")} content={t("There are no deposits available for the selected customer")} icon="magnifying-glass" />
                ) : <div><div className="customer-deposits-modal ui-checkbox-group">
                    {deposits?.map((deposit, index) => {
                        return (
                            <div
                                key={index}
                                onClick={() => {
                                    ReactDOM.unstable_batchedUpdates(() => {
                                        setApplyAmount(Math.min(Number(cartAmount), deposit?.amount_remaining)?.toString());
                                        setSelectedDeposit(deposit);
                                    });

                                }}
                                className={classNames("ui-checkbox-group-item", {
                                    "ui-checkbox-group-item_selected": selectedDeposit?.id === deposit?.id,
                                })}
                            >
                                <div className="ui-checkbox-group-item-content">
                                    <div className="ui-checkbox-group-item-text">
                                        <p className="text-semibold"><LocaleCurrency currency={deposit?.currency ?? "CAD"} amount={deposit?.amount_remaining} /></p>
                                        <p><span className="text-semibold">{t("Deposit completed: ")}</span> {moment(deposit?.processed_at_local)?.format("LL")}</p>
                                        {deposit?.booking?.tee_time ? (
                                            <p><span className="text-semibold">{t("Tee time: ")}</span> {moment(`${deposit?.booking?.tee_time?.date} ${deposit?.booking?.tee_time?.start_time}`, "YYYY-MM-DD HH:mm")?.format("LLL")}</p>
                                        ) : null}
                                        
                                    </div>
                                </div>
                                <Checkbox
                                    size="medium"
                                    value={deposit.id}
                                    checked={selectedDeposit?.id === deposit?.id}
                                    onChange={() => {
                                        ReactDOM.unstable_batchedUpdates(() => {
                                            setApplyAmount(Math.min(Number(cartAmount), deposit?.amount_remaining)?.toString());
                                            setSelectedDeposit(deposit);
                                        });

                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
                    <div className="customer-deposits-modal-input">
                        <Input
                            label={t("Amount to apply")}
                            placeholder={"0.00"}
                            prefix={getCurrencySymbol(selectedDeposit?.currency) ?? "$"}
                            value={applyAmount}
                            onChange={e => {
                                if (Number(e.target.value) > Math.min(Number(cartAmount), selectedDeposit?.amount_remaining)) {
                                    return;
                                }
                                setApplyAmount(e.target.value);
                            }}
                            disabled={!selectedDeposit}
                            error={Number(applyAmount) <= 0 || Number(applyAmount) > Math.min(Number(cartAmount), selectedDeposit?.amount_remaining)}
                            type="number"
                        />
                    </div>
                </div>}
            </Sheet>
        </Portal>
    );
}